import { Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "i18n-fe";

// #860 — canonical ride identity, adopted at every site that renders a ride
// (ClassDetailDrawer, RideHistoryDrawer, ReviewQueue, ClassPlacings,
// RideEditor's RidePicker, ReportsPage) so the same ride reads the same way
// everywhere and — outside pickers/autocompletes, where a link makes no
// sense — is always clickable straight into the RideEditor detail route
// (App.jsx `/rides/:rideId`). Degrades gracefully as fields drop out; never
// renders the literal "undefined".
//
// `formatRideWho`/`formatRideLabel` are exported as pure formatters for
// sites that need the same text without the link (RidePicker's MenuItem
// options, ReportsPage's Autocomplete lookup label) — see the card's note
// that pickers "may keep their own shape... but should use the same
// formatter."
export function formatRideWho(ride, onWord = "on") {
  if (!ride) return null;
  const rider = ride.riderName || null;
  const horse = ride.horseName || null;
  return rider && horse ? `${rider} ${onWord} ${horse}` : rider || horse || null;
}

// Canonical "#CNO, Rider on Horse" — degrades to whichever pieces are
// present (just the CNO, just the who, or null if the ride has neither).
// #903 — deliberately keyed off competitorNumber (CNO), never rideNumber:
// rideNumber is FVD's tblRide.intForceOrder, the running-order sequence
// number, which a late-added/manual ride never receives (FVD only assigns
// it when the day sheet's order is generated — see
// apps/foxbox/src/read/mapper.js's mapRide) and so can be legitimately
// absent. CNO is derived straight from the entry (lngEntryID) and is always
// present, so using it as the "#" prefix means a ride missing rideNumber
// still gets a clear, stable identifier instead of rendering bare.
export function formatRideLabel(ride, onWord = "on") {
  if (!ride) return null;
  const cno = ride.competitorNumber != null ? `#${ride.competitorNumber}` : null;
  const who = formatRideWho(ride, onWord);
  return [cno, who].filter(Boolean).join(", ") || null;
}

// `ride` carries at least { riderName, horseName, competitorNumber } plus an
// id (`ride.id` or `ride.rideId` — callers' shapes differ); `rideId` can be
// passed explicitly when the caller only has the id half of a join miss.
// `fallback` renders (unlinked) when there's no id to link to or no label
// text at all.
// #3563 — `children` replaces the ride label with other link text (an "Open in
// Ride Editor" link next to a reason), and `sheetId` adds `?sheet=<id>` so the
// editor opens on that one judge's sheet (RideEditor reads it at mount).
export default function RideLink({ ride, rideId, sheetId, children, fallback = "—", sx, ...linkProps }) {
  const { t } = useTranslation("scoreadmin");
  const id = rideId ?? ride?.rideId ?? ride?.id ?? null;
  const label = children ?? (formatRideLabel(ride, t("rideEditor.onHorse")) || fallback);

  if (!id) return label;

  return (
    <Link
      component={RouterLink}
      to={sheetId ? `/rides/${id}?sheet=${encodeURIComponent(sheetId)}` : `/rides/${id}`}
      color="text.primary"
      underline="always"
      sx={sx}
      {...linkProps}
    >
      {label}
    </Link>
  );
}
